import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCenaHistoria } from "@/api/hooks/useZakazky";
import { formatDateTime, formatEur } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { CenaSnapshot } from "@/api/schemas";

/**
 * História cien zákazky — snímka súčtu pri každom prechode stavu.
 *
 * Ceny sa počítajú **on-demand z aktuálnych dát**, takže súčet z marca sa
 * v auguste nedá dopočítať: cenník sa medzitým zmenil a dielce tiež. Toto
 * je jediné miesto, kde sa dá zistiť „za koľko sme to vtedy ponúkli".
 */
export function CenaHistoriaDialog({
  open,
  onOpenChange,
  zakazkaId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  zakazkaId: string;
}): JSX.Element {
  const query = useCenaHistoria(zakazkaId, open);
  const snapshoty = query.data?.data?.snapshoty ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>História cien zákazky</DialogTitle>
          <DialogDescription className="sr-only">
            Za koľko bola zákazka ocenená pri každom prechode stavu.
          </DialogDescription>
        </DialogHeader>

        <p className="text-xs text-muted-foreground">
          Snímka súčtu pri každom prechode stavu. Ceny sa počítajú
          z aktuálneho cenníka, takže staršiu cenu sa inak dopočítať nedá —
          tieto čísla sa už nemenia.
        </p>

        {query.isLoading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Načítavam…
          </p>
        ) : snapshoty.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Zákazka ešte nezmenila stav — žiadna snímka ceny.
          </p>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[150px]">Kedy</TableHead>
                  <TableHead className="w-[110px]">Kto</TableHead>
                  <TableHead>Prechod</TableHead>
                  <TableHead className="text-right">Rozsah</TableHead>
                  <TableHead className="text-right">S DPH</TableHead>
                  <TableHead className="text-right">Rozdiel</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {snapshoty.map((s) => (
                  <Riadok key={s.id} s={s} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Riadok({ s }: { s: CenaSnapshot }): JSX.Element {
  const rozdiel = s.rozdiel_s_dph == null ? null : Number(s.rozdiel_s_dph);
  return (
    <TableRow data-testid={`snapshot-${s.id}`}>
      <TableCell className="text-xs text-muted-foreground">
        {formatDateTime(s.created_at)}
      </TableCell>
      <TableCell className="text-xs">{s.autor}</TableCell>
      <TableCell className="text-sm">{s.prechod}</TableCell>
      <TableCell
        className="text-right text-xs text-muted-foreground"
        title="Zostáv a dielcov v tom momente — bez toho sa nedá povedať, či cena stúpla kvôli cenníku alebo kvôli pribudnutým dielcom"
      >
        {s.zostav} zost. · {s.dielcov} diel.
      </TableCell>
      <TableCell className="text-right font-mono text-sm font-medium">
        {formatEur(s.cena_s_dph)}
      </TableCell>
      <TableCell className="text-right font-mono text-sm">
        {rozdiel == null || rozdiel === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span
            className={cn(
              "font-medium",
              rozdiel > 0 ? "text-destructive" : "text-emerald-700",
            )}
          >
            {rozdiel > 0 ? "+" : "−"}
            {formatEur(Math.abs(rozdiel))}
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}
